import React from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

export default function DateTimePickers({
  selectedDates,
  time,
  handleDateChange,
  handleDeleteWithConfirmation,
  updateTime,
  addTimePicker,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Date Selection */}
      <Box>
        <Typography
          variant='h6'
          gutterBottom
          sx={{ 
            fontWeight: 900,
            color: '#333',
            mb: 2,
            fontSize: '1.1rem'
          }}
        >
          Select Dates
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            width: '100%',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              displayStaticWrapperAs='desktop'
              value={selectedDates}
              onChange={handleDateChange}
              shouldDisableDate={(date) =>
                dayjs(date).isBefore(dayjs(), 'day')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    backgroundColor: 'white',
                    border: 'none',
                    '& .MuiPickersDay-root': {
                      borderRadius: '50%',
                    },
                  }}
                />
              )}
              renderDay={(day, _, pickersProps) => {
                const isSelected = selectedDates.some((date) =>
                  dayjs(date).isSame(day, 'day')
                );

                return (
                  <PickersDay
                    {...pickersProps}
                    day={day}
                    selected={isSelected}
                  />
                );
              }}
              className='custom-calendar'
              sx={{ height: 'auto', minHeight: '300px' }}
            />
          </LocalizationProvider>
        </Box>
        {selectedDates.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: '#555',
                mb: 1.5,
                fontSize: '0.95rem'
              }}
            >
              Selected Dates:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedDates?.map((date, index) => (
                <Chip
                  key={index}
                  label={date}
                  onDelete={() => handleDeleteWithConfirmation('date', index)}
                  variant="outlined"
                  sx={{
                    borderColor: '#9291f0',
                    color: '#9291f0',
                    '& .MuiChip-deleteIcon': {
                      color: '#d32f2f',
                      '&:hover': {
                        color: '#b71c1c',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Time Selection */}
      <Box>
        <Typography
          variant='h6'
          gutterBottom
          sx={{ 
            fontWeight: 900,
            color: '#333',
            mb: 2,
            fontSize: '1.1rem'
          }}
        >
          Select Time
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {time?.slice(0, -1).map((timeValue, index) => (
            <Box 
              key={index} 
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  value={dayjs(timeValue, 'HH:mm A')}
                  onChange={(val) => updateTime(index, val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ style: { color: 'gray' } }}
                      sx={{
                        backgroundColor: 'white',
                        minWidth: '150px'
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              <IconButton 
                onClick={() => handleDeleteWithConfirmation('time', index)}
              >
                <DeleteIcon color='error' />
              </IconButton>
            </Box>
          ))}

          {time && time.length > 0 && (
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  value={dayjs(time[time.length - 1], 'HH:mm A')}
                  onChange={(val) => updateTime(time.length - 1, val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputLabelProps={{ style: { color: 'gray' } }}
                      sx={{
                        backgroundColor: 'white',
                        minWidth: '150px'
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              <IconButton 
                onClick={() => handleDeleteWithConfirmation('time', time.length - 1)}
              >
                <DeleteIcon color='error' />
              </IconButton>

              <Button
                variant='contained'
                onClick={addTimePicker}
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: '#9291f0',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#7a79e0',
                  },
                }}
              >
                Add Time
              </Button>
            </Box>
          )}

          {(!time || time.length === 0) && (
            <Button
              variant='contained'
              onClick={addTimePicker}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#9291f0',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#7a79e0',
                },
                alignSelf: 'flex-start',
                mt: 1
              }}
            >
              Add Time
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
